import { useFormik } from 'formik';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Col, Form, Modal, Row, Spinner,
} from 'react-bootstrap';
import * as yup from 'yup';
import { api } from '../../../../api';
import { TextField } from '../../../../ui/_fields/TextField/TextField';
import { PROMOTION_IDENTIFIER_MAX_LENGTH } from '../../utils/promotionUtils';
import { PromotionDateTimeField } from './PromotionDateTimeField';
import { PromotionStoryUuidField } from './PromotionStoryUuidField';
import styles from './PromotionForm.module.scss';

export interface PromotionFormValues {
  title: string;
  identifier: string;
  storyUuid: string;
  disabled: boolean;
  startTime: string | null;
  endTime: string | null;
}

interface PromotionFormProps {
  initialValues: PromotionFormValues;
  originalIdentifier?: string;
  requireDates: boolean;
  saving?: boolean;
  isNewPromotion?: boolean;
  onSubmit: (values: PromotionFormValues) => void;
  onUnsavedPromoTypeFieldsChange?: (hasUnsaved: boolean) => void;
}

const identifierPattern = /^[a-z0-9_-]+$/;
const storyUuidPattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

type BookLookupResponse = { success: boolean; book: { title: string } | null };

async function validateStoryUuidValue(value: string | null | undefined) {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) {
    return true;
  }
  if (trimmed.length !== 36 || !storyUuidPattern.test(trimmed)) {
    return false;
  }
  const response = await api.get<BookLookupResponse>(
    `/v1/books/by-uuid/${encodeURIComponent(trimmed)}`,
  );
  return Boolean(response.data?.book);
}

function sameNullableString(a: string | null | undefined, b: string | null | undefined) {
  return (a || '') === (b || '');
}

function hasUnsavedPromoTypeFields(
  values: PromotionFormValues,
  initialValues: PromotionFormValues,
) {
  return values.identifier !== initialValues.identifier
    || !sameNullableString(values.storyUuid, initialValues.storyUuid)
    || !sameNullableString(values.startTime, initialValues.startTime)
    || !sameNullableString(values.endTime, initialValues.endTime);
}

function buildSchema(requireDates: boolean) {
  return yup.object({
    title: yup.string().trim().required('Title is required'),
    identifier: yup.string()
      .trim()
      .required('Identifier is required')
      .max(PROMOTION_IDENTIFIER_MAX_LENGTH, `Identifier must be at most ${PROMOTION_IDENTIFIER_MAX_LENGTH} characters`)
      .matches(identifierPattern, 'Use letters, numbers, hyphens, and underscores only'),
    storyUuid: yup.string()
      .test(
        'story-uuid-length',
        'Story UUID must be 36 characters or empty',
        (value) => {
          const trimmed = String(value ?? '').trim();
          return !trimmed || trimmed.length === 36;
        },
      )
      .test(
        'story-uuid-book',
        'Story UUID does not match a book',
        (value) => validateStoryUuidValue(value),
      ),
    disabled: yup.boolean(),
    startTime: requireDates
      ? yup.string().nullable().required('Start time is required')
      : yup.string().nullable(),
    endTime: requireDates
      ? yup.string().nullable().required('End time is required')
      : yup.string().nullable(),
  }).test('end-after-start', 'End time must be after start time', (values) => {
    if (!values?.startTime || !values?.endTime) {
      return true;
    }
    return moment(values.endTime).isAfter(moment(values.startTime));
  });
}

export function PromotionForm(props: PromotionFormProps) {
  const {
    initialValues,
    originalIdentifier,
    requireDates,
    saving = false,
    isNewPromotion = false,
    onSubmit,
    onUnsavedPromoTypeFieldsChange,
  } = props;
  const [showIdentifierConfirm, setShowIdentifierConfirm] = useState(false);
  const [pendingValues, setPendingValues] = useState<PromotionFormValues | null>(null);

  const formik = useFormik<PromotionFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema: buildSchema(requireDates),
    onSubmit: (values) => {
      const normalized = {
        ...values,
        identifier: values.identifier.trim().toLowerCase(),
        storyUuid: values.storyUuid.trim(),
      };
      if (
        originalIdentifier
        && normalized.identifier !== originalIdentifier
      ) {
        setPendingValues(normalized);
        setShowIdentifierConfirm(true);
        return;
      }
      onSubmit({
        ...normalized,
        storyUuid: normalized.storyUuid || '',
      });
    },
  });

  const startMoment = formik.values.startTime ? moment(formik.values.startTime) : null;
  const minEndTime = startMoment ? startMoment.clone().add(1, 'minute').toDate() : new Date(0);
  const submitLabel = isNewPromotion ? 'Save' : 'Update';
  const submitDisabled = saving || (!isNewPromotion && !formik.dirty);
  const showUnsavedPromoTypeWarning = !isNewPromotion
    && hasUnsavedPromoTypeFields(formik.values, formik.initialValues);

  useEffect(() => {
    onUnsavedPromoTypeFieldsChange?.(showUnsavedPromoTypeWarning);
    return () => onUnsavedPromoTypeFieldsChange?.(false);
  }, [onUnsavedPromoTypeFieldsChange, showUnsavedPromoTypeWarning]);

  return (
    <>
      <Form noValidate onSubmit={formik.handleSubmit}>
        <Row>
          <Col md={6}>
            <TextField
              label="Title"
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              error={formik.touched.title ? formik.errors.title : undefined}
            />
          </Col>
          <Col md={6}>
            <TextField
              label="Identifier"
              name="identifier"
              value={formik.values.identifier}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                formik.setFieldValue('identifier', event.target.value.toLowerCase());
              }}
              maxLength={PROMOTION_IDENTIFIER_MAX_LENGTH}
              error={formik.touched.identifier ? formik.errors.identifier : undefined}
            />
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={12}>
            <PromotionStoryUuidField
              value={formik.values.storyUuid}
              onChange={formik.handleChange}
              onBlur={() => formik.setFieldTouched('storyUuid', true)}
              error={formik.touched.storyUuid ? formik.errors.storyUuid : undefined}
              disabled={saving}
            />
          </Col>
        </Row>

        <Row className={styles.scheduleRow}>
          <Col md={4}>
            <PromotionDateTimeField
              label="Start time"
              value={formik.values.startTime || ''}
              onChange={(date) => formik.setFieldValue('startTime', date.toISOString())}
              minDateTime={new Date(2000, 0, 1)}
              error={formik.touched.startTime ? formik.errors.startTime : undefined}
            />
          </Col>
          <Col md={4}>
            <PromotionDateTimeField
              label="End time"
              value={formik.values.endTime || ''}
              onChange={(date) => formik.setFieldValue('endTime', date.toISOString())}
              minDateTime={minEndTime}
              error={formik.touched.endTime ? formik.errors.endTime : undefined}
            />
          </Col>
          <Col md={4} className={styles.disabledCol}>
            <Form.Check
              className={styles.disabledCheck}
              type="checkbox"
              id="promotion-disabled"
              label="Disabled (overrides schedule)"
              checked={formik.values.disabled}
              onChange={(event) => formik.setFieldValue('disabled', event.target.checked)}
              disabled={saving}
            />
          </Col>
        </Row>

        <div className={styles.submitRow}>
          <Button type="submit" variant="primary" disabled={submitDisabled}>
            {saving ? <Spinner animation="border" size="sm" /> : submitLabel}
          </Button>
          {showUnsavedPromoTypeWarning && (
            <Alert variant="warning" className={styles.unsavedWarning}>
              You have unsaved changes.
            </Alert>
          )}
        </div>
      </Form>

      <Modal
        show={showIdentifierConfirm}
        onHide={() => setShowIdentifierConfirm(false)}
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Change identifier?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Changing the identifier can break links and integrations that reference
          {' '}
          <code>{originalIdentifier}</code>
          . Are you sure you want to continue?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowIdentifierConfirm(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              if (pendingValues) {
                onSubmit(pendingValues);
              }
              setShowIdentifierConfirm(false);
              setPendingValues(null);
            }}
          >
            Change identifier
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
